/**
 * Schema of the device-local database (`QUIPWA:local` in IndexedDB).
 *
 * Unlike `_state.db`, this database is **never uploaded**. It holds what only
 * makes sense on this device:
 *
 *   kv      — small values: sync bookmarks (last Drive version seen per file),
 *             owner account, pre-generated Drive ids…
 *   outbox  — pending writes to Drive, drained by the sync engine
 *   notes…  — the notes index and cached bodies (see migration 2)
 *
 * Keeping it separate keeps `_state.db` small (note bodies are already in Drive
 * as .md files) and avoids replicating device-specific bookkeeping.
 */
import type { Migration, SchemaDefinition } from '../migrations.ts'

export const LOCAL_BASELINE_VERSION = 1

export const LOCAL_DB_SCHEMA = `
CREATE TABLE IF NOT EXISTS kv (
  key        TEXT    PRIMARY KEY,
  value      TEXT    NOT NULL,
  updated_at INTEGER NOT NULL
) STRICT;

CREATE TABLE IF NOT EXISTS outbox (
  id              INTEGER PRIMARY KEY AUTOINCREMENT,
  kind            TEXT    NOT NULL,
  -- Operations with the same (kind, entity_key) coalesce: only the latest
  -- payload survives, so ten saves of one note upload once.
  entity_key      TEXT    NOT NULL,
  payload         TEXT    NOT NULL,
  -- Bumped on every enqueue; completing an op only deletes it if nobody
  -- re-enqueued it while it was being processed.
  revision        INTEGER NOT NULL DEFAULT 1,
  created_at      INTEGER NOT NULL,
  attempts        INTEGER NOT NULL DEFAULT 0,
  next_attempt_at INTEGER NOT NULL DEFAULT 0,
  last_error      TEXT,
  UNIQUE (kind, entity_key)
) STRICT;

CREATE INDEX IF NOT EXISTS idx_outbox_due ON outbox(next_attempt_at);
`

export const LOCAL_MIGRATIONS: Migration[] = [
  {
    version: 2,
    description: 'Local-first notes: index, cached bodies and the merge base per note',
    up: async (db) => {
      // One row per note. `body` is the whole .md file (frontmatter + text) as
      // this device has it; `base`/`base_version` are the last version known to
      // be on Drive — the common ancestor for the three-way merge.
      //
      // `id` is the note's stable id. It equals the Drive file id for notes that
      // exist on Drive or were created with a pre-generated id; offline-created
      // notes without one get `local-…` and keep it (Drive stores it in the
      // file's appProperties.quipwaId), so routes and links never change.
      await db.run(`
        CREATE TABLE IF NOT EXISTS notes (
          id            TEXT    PRIMARY KEY,
          drive_file_id TEXT,
          parent_id     TEXT,
          folder_id     TEXT,
          filename      TEXT    NOT NULL,
          title         TEXT    NOT NULL,
          type          TEXT    NOT NULL DEFAULT 'Quick Note',
          status        TEXT    NOT NULL DEFAULT 'Pending',
          domain        TEXT    NOT NULL DEFAULT '',
          tags          TEXT    NOT NULL DEFAULT '[]',
          body          TEXT    NOT NULL DEFAULT '',
          base          TEXT,
          base_version  TEXT,
          modified_at   INTEGER NOT NULL,
          local_rev     INTEGER NOT NULL DEFAULT 0,
          dirty         INTEGER NOT NULL DEFAULT 0,
          deleted       INTEGER NOT NULL DEFAULT 0
        ) STRICT
      `)
      await db.run('CREATE UNIQUE INDEX IF NOT EXISTS idx_notes_drive ON notes(drive_file_id) WHERE drive_file_id IS NOT NULL')
      await db.run('CREATE INDEX IF NOT EXISTS idx_notes_parent ON notes(parent_id)')
    },
  },
]

export const LOCAL_SCHEMA: SchemaDefinition = {
  baseline: LOCAL_DB_SCHEMA,
  baselineVersion: LOCAL_BASELINE_VERSION,
  migrations: LOCAL_MIGRATIONS,
}
